/**
 * How the rules work, in the scheduler's words.
 *
 * The vocabulary screen has always had the right information and required you
 * to already know the system to read it — compiler_key, allowed selectors,
 * normalizer. This page is the thing that was missing: what a rule IS, what the
 * two kinds do differently, and what happens when two of them disagree.
 *
 * It is written, not generated. A generated page would list the terms, which is
 * what the vocabulary screen already does; what is needed is the model behind
 * them, and nothing in the database holds that.
 */
export function Rules({ go }: { go: (view: string) => void }) {
  return (
    <div className="doc">
      <p className="hint" style={{ marginTop: 0 }}>
        You never have to write a rule. You ask for something in plain words, and the
        assistant turns it into rules from the list on the Vocabulary screen. This page is
        for when you want to know what it did, or why it refused.
      </p>

      <h2>What a rule is</h2>
      <p>
        A rule is one sentence about the schedule that the optimizer has to take into
        account — “Eric must not go above 100%”, “this project finishes by the 12th”,
        “Aisha does not work on Lowe jobs”. When you ask for something, your request is
        turned into one or more of these, and they are listed on the board so you can see
        exactly what the plan is being held to.
      </p>
      <p>
        The list of rules the assistant is allowed to use is fixed. It cannot invent one. If
        you ask for something no rule expresses, it says so rather than quietly doing
        something else — that is what “unmapped” means when you see it.
      </p>

      <h2>The two kinds</h2>
      <div className="eg">
        <b>Must</b> — a hard rule. The plan is not allowed to break it. If two of them
        cannot both be true, there is no plan at all, and you are told which ones are
        fighting.
        <br /><br />
        <b>Prefer</b> — a soft rule. The optimizer trades these off against each other. Each
        one carries a weight, and a heavier one wins more arguments.
      </div>
      <p>
        This is the single most useful thing to know. “Finish by the 12th” as a <b>must</b>
        {" "}will refuse to produce a plan if it is impossible. The same thing as a{" "}
        <b>prefer</b> will produce a plan that is as close to the 12th as it can manage. Most
        frustration with the optimizer is a must that wanted to be a prefer.
      </p>

      <h2>What a rule applies to</h2>
      <p>
        Every rule is pointed at some work. That can be one step, a whole project, everything
        of one work type, everything for one customer, one person's queue, or simply
        everything. On the Vocabulary screen this is called <b>what it can be applied to</b> —
        the list of things that particular rule is allowed to be pointed at.
      </p>
      <p>
        If a rule is pointed at something that matches nothing — a mistyped project number,
        say — the plan is refused rather than produced without it. A rule that quietly applied
        to nothing would look exactly like a rule that worked.
      </p>

      <h2>Weights, and why they are comparable</h2>
      <p>
        Soft rules measure different things — days late, days to finish, how many people had
        their work changed. Those cannot be added together as they are, so each is first
        converted to a number between 0 and 1 by dividing it by something sensible: lateness
        by the length of the horizon, churn by how many steps were in scope. That divisor is
        the <b>normalizer</b> on the Vocabulary screen. It is why a weight of 1.0 means the
        same strength of preference regardless of which rule it is on.
      </p>

      <h2>When two rules disagree</h2>
      <p>
        Two <b>musts</b> that cannot both hold produce no plan, and the optimizer names the
        rules involved. That naming is the useful part: it is not “this is impossible”, it is
        “these three, together, are impossible”. Turn one off, or soften it to a prefer, and
        ask again.
      </p>
      <p>
        A <b>must</b> always beats a <b>prefer</b>. Pinning a step to somebody will override
        the preference to keep the plan stable, and it will do so without warning — that is
        what pinning means.
      </p>

      <h2>The parts of a rule on the Vocabulary screen</h2>
      <table className="qt">
        <thead><tr><th>What it says</th><th>What it means</th></tr></thead>
        <tbody>
          <tr>
            <td><b>What it changes</b></td>
            <td>
              The effect in one sentence, and the value it overrides. Written by
              engineering alongside the code that implements it, so it cannot describe
              something the system stopped doing.
            </td>
          </tr>
          <tr>
            <td><b>What it can be applied to</b></td>
            <td>The kinds of thing this rule may be pointed at — a step, a project, a person, everything.</td>
          </tr>
          <tr>
            <td><b>Limits</b></td>
            <td>
              The range the assistant is allowed to ask for. “Permit overtime” accepts 80% to
              140%; ask for 200% and it is refused rather than clamped silently.
            </td>
          </tr>
          <tr>
            <td><b>Strength</b> <span className="muted">(prefer only)</span></td>
            <td>How hard this preference argues, against the other preferences.</td>
          </tr>
          <tr>
            <td><b>Phrasings</b></td>
            <td>
              Examples of how somebody might ask for this. They are shown to the assistant so
              it recognizes the request. Adding a phrasing you actually use makes it more
              reliable at understanding you.
            </td>
          </tr>
          <tr>
            <td><b>Built-in name</b></td>
            <td>
              The identifier of the code that implements the rule. Shown so engineering and
              you are talking about the same thing, and not editable — repointing a rule at
              different code would give it a name that lies about what it does.
            </td>
          </tr>
        </tbody>
      </table>

      <h2>Why a rule can be switched off</h2>
      <p>
        Some rules are listed but off. Usually that is because the optimizer has no
        implementation for them yet — the rule would be accepted, and then ignored, which is
        worse than refusing it. Where that is the case the screen says so under{" "}
        <b>why it is off</b>. Turning such a rule on is not useful until engineering has
        built the other half, and the screen will keep saying <b>blocked</b>.
      </p>

      <h2>What to do when it will not do what you asked</h2>
      <ol>
        <li>Read the rules listed on the board. Your request is in there, in its own words.</li>
        <li>If one is a <b>must</b> that should be a <b>prefer</b>, say so — “try to” rather than “make sure”.</li>
        <li>If it says terms matched nothing, a project or person in your request did not exist. Check the number.</li>
        <li>If it says the rules conflict, it will name them. Switch one off and ask again.</li>
      </ol>

      <p style={{ marginTop: 24 }}>
        <button className="btn" onClick={() => go("dsl")}>Back to the vocabulary</button>
      </p>
    </div>
  );
}
